/**
 * /pair/:requestId: where the Orgus installer sends the browser to approve a computer (ADR 0042
 * decision 1, R-EN-1, R-ON-3). Not in the nav.
 *
 * The installer opens `/pair/<id>#k=<code>`. The code is read from the fragment (never sent in a
 * URL), kept in this tab only across a sign-in, and stripped from the address bar. Signed out,
 * the sign-in screen shows and comes back here. Signed in, the code shows the computer's facts
 * (peek, which claims nothing) and the request is opened (.../claim) only when the person clicks
 * Open request (PAIR-WEB-2: a link someone sent cannot plant a request in their console). Then one
 * card: the facts, its key fingerprint, who it joins, Approve. Opened without a code (Flow C, the
 * link the computer prints), the person types the three words the computer shows. Every state has
 * its view: loading, opening, words to type, the request opened elsewhere, approved, expired,
 * declined, stopped, cancelled, not allowed, and errors with Retry.
 */
import { CircleSlash, Clock, Link2Off, OctagonX, ShieldAlert } from '@orgus/ui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router';
import { isPairFinal } from '@orgus/protocol';
import type { PairRequestView } from '@orgus/protocol';
import { TooltipProvider, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { AuthGate } from '../features/auth/AuthGate.tsx';
import { approvePairRequest, claimPairRequest, denyPairRequest, getPairRequest, peekPairRequest } from '../features/pairing/api.ts';
import { authClient, signInButtons } from '../features/auth/client.ts';
import { useMe } from '../features/auth/useMe.ts';
import { startData } from '../lib/data.ts';
import { MOCK_ERROR, MOCK_NOT_ALLOWED, MOCK_PAIR_PROVIDERS, mockPairCase, mockPairView } from '../features/pairing/mock.ts';
import { capturePairCode, claimOnce, forgetPairCode, limitPairCode, viewWithCode } from '../features/pairing/pairCode.ts';
import { PairAwaitingAdmin, PairConsent, PairEnded, PairError, PairFrame, PairLoading, PairNoCode, PairNotAllowed, PairNotice, PairOpen, PairSignIn, PairWords } from '../features/pairing/PairView.tsx';
import { isMockRequested } from '../lib/mock.ts';

/** While a request is open, look again this often: it may expire or be decided elsewhere. */
const REFRESH_MS = 5_000;


/** A refusal the server explained (the request ended, or this person may not decide it), as a notice. */
function refusalNotice(e: UiError): ReactNode {
  const icon = e.code === 'expired' ? <Clock /> : e.code === 'conflict' ? <ShieldAlert /> : e.code === 'bad-k' ? <Link2Off /> : e.code === 'wrong-words' || e.code === 'already-decided' ? <OctagonX /> : <CircleSlash />;
  return <PairNotice icon={icon} tone={e.code === 'conflict' ? 'danger' : 'neutral'} title={e.what} why={e.why} next={e.next} />;
}

/** What to show for a request and the person looking at it. */
export interface PairScreenState {
  phase: 'loading' | 'ready' | 'refused' | 'error';
  view: PairRequestView | null;
  error: UiError | null;
  /** The request is open in the browser flow, and this tab has no code for it. */
  noCode: boolean;
  /** The code showed the request's facts; it is opened (claimed) only on a click. */
  toOpen?: boolean;
}

export function PairScreen(p: {
  s: PairScreenState;
  name: string;
  onName: (v: string) => void;
  autoUpdate: boolean;
  onAutoUpdate: (v: boolean) => void;
  busy: 'approve' | 'deny' | 'claim' | null;
  actionError: UiError | null;
  onApprove: () => void;
  onDeny: () => void;
  /** The person opened the request; `differentNetworkAck`: they confirmed it is their computer (review I2). */
  onOpen: (differentNetworkAck: boolean) => void;
  onWords: (w: [string, string, string]) => void;
  onRetry: () => void | Promise<void>;
}) {
  const { s } = p;
  if (s.phase === 'loading') return <PairLoading />;
  if (s.phase === 'error') return <PairError error={s.error} onRetry={p.onRetry} />;
  // Without a code, a request nobody opened yet is not found by its id: it is opened with the words on the computer.
  if (s.phase === 'refused' && s.error?.code === 'not-found' && s.noCode) return <PairWords busy={p.busy === 'claim'} actionError={p.actionError} onWords={p.onWords} />;
  if (s.phase === 'refused' && s.error) return s.error.status === 403 && !s.error.code?.startsWith('bad') ? <PairNotAllowed error={s.error} /> : <>{refusalNotice(s.error)}</>;
  const v = s.view;
  if (!v) return <PairLoading />;
  if (isPairFinal(v.state)) return <PairEnded view={v} />;
  if (v.state === 'unclaimed' && s.toOpen) return <PairOpen view={v} busy={p.busy === 'claim'} actionError={p.actionError} onOpen={p.onOpen} />;
  if (v.state === 'unclaimed') return v.flow === 'words' ? <PairWords busy={p.busy === 'claim'} actionError={p.actionError} onWords={p.onWords} /> : <PairNoCode />;
  if (v.awaitingAdmin) return <PairAwaitingAdmin view={v} />;
  if (!v.canDecide) {
    return <PairNotAllowed error={{ what: 'This request was opened by someone else', why: `${v.claimedBy?.name ?? 'Another person'} opened it on the computer; an admin approves it.`, next: 'Ask an admin to finish adding the computer.' }} />;
  }
  return (
    <PairConsent
      view={v}
      name={p.name}
      onName={p.onName}
      autoUpdate={p.autoUpdate}
      onAutoUpdate={p.onAutoUpdate}
      busy={p.busy === 'claim' ? null : p.busy}
      actionError={p.actionError}
      onApprove={p.onApprove}
      onDeny={p.onDeny}
    />
  );
}

function usePairRequest(requestId: string, code: string | null) {
  const [s, setS] = useState<PairScreenState>({ phase: 'loading', view: null, error: null, noCode: !code });
  const [busy, setBusy] = useState<'approve' | 'deny' | 'claim' | null>(null);
  const [actionError, setActionError] = useState<UiError | null>(null);
  const [name, setName] = useState('');
  const [autoUpdate, setAutoUpdate] = useState(true);
  const named = useRef(false);

  const show = useCallback((view: PairRequestView, toOpen = false) => {
    if (!named.current) {
      named.current = true;
      setName(view.machineName ?? view.machine.name ?? view.machine.hostname);
    }
    limitPairCode(view.requestId, view.expiresAt);
    if (isPairFinal(view.state)) forgetPairCode(view.requestId);
    setS({ phase: 'ready', view, error: null, noCode: !code, toOpen });
  }, [code]);

  const fail = useCallback((e: unknown) => {
    const ui = toUiError(e);
    const refused = ui.status !== undefined && [403, 404, 409, 410].includes(ui.status);
    if (refused) forgetPairCode(requestId);
    setS((prev) => ({ ...prev, phase: refused ? 'refused' : 'error', error: ui }));
  }, [requestId]);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      if (code) {
        // The code shows what is asking (it goes in the body only). A request nobody opened yet is
        // opened only by a click (PAIR-WEB-2); one this session opened before reopens at once.
        const { view, toOpen } = await viewWithCode(requestId, code);
        show(view, toOpen);
      } else {
        show(await getPairRequest(requestId, signal));
      }
    } catch (e) {
      if (!signal?.aborted) fail(e);
    }
  }, [code, fail, requestId, show]);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Live: an open request may expire or be decided in another tab.
  const open = s.phase === 'ready' && s.view !== null && !isPairFinal(s.view.state) && !s.toOpen;
  useEffect(() => {
    if (!open || busy) return;
    const t = setInterval(() => {
      getPairRequest(requestId).then(show, () => undefined);
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [open, busy, requestId, show]);

  const act = useCallback(
    async (kind: 'approve' | 'deny' | 'claim', run: () => Promise<PairRequestView>) => {
      setBusy(kind);
      setActionError(null);
      try {
        show(await run());
      } catch (e) {
        const ui = toUiError(e);
        // The request ended meanwhile: show where it stands instead of a stale card.
        if (ui.status === 409 || ui.status === 410) fail(e);
        else setActionError(ui);
      } finally {
        setBusy(null);
      }
    },
    [fail, show],
  );

  return {
    s,
    busy,
    actionError,
    name,
    setName,
    autoUpdate,
    setAutoUpdate,
    retry: () => {
      setS((prev) => ({ ...prev, phase: 'loading' }));
      return load();
    },
    approve: () => act('approve', () => approvePairRequest(requestId, { name: name.trim() || undefined, autoUpdate })),
    deny: () => act('deny', () => denyPairRequest(requestId)),
    open: (differentNetworkAck = false) => (code ? act('claim', () => claimOnce(requestId, code, (id, body) => claimPairRequest(id, differentNetworkAck ? { ...body, differentNetworkAck: true } : body))) : undefined),
    words: (words: [string, string, string]) => act('claim', () => claimPairRequest(requestId, { words })),
  };
}

function LivePair({ requestId, code }: { requestId: string; code: string | null }) {
  const r = usePairRequest(requestId, code);
  return (
    <PairScreen
      s={r.s}
      name={r.name}
      onName={r.setName}
      autoUpdate={r.autoUpdate}
      onAutoUpdate={r.setAutoUpdate}
      busy={r.busy}
      actionError={r.actionError}
      onApprove={() => void r.approve()}
      onDeny={() => void r.deny()}
      onOpen={(ack) => void r.open(ack)}
      onWords={(w) => void r.words(w)}
      onRetry={r.retry}
    />
  );
}

/**
 * Signed out: "Sign in to approve <machine>" with what the code from the link shows (peek), and
 * the sign-in methods the server offers. Sign-in returns to /pair/<id> (the path only: the code
 * stays in this tab), where the page claims by itself.
 */
function SignedOutPair({ requestId, code }: { requestId: string; code: string | null }) {
  const providers = useMe((s) => s.providers);
  const [view, setView] = useState<PairRequestView | null>(null);
  const [refusal, setRefusal] = useState<UiError | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => {
    if (!code) return;
    peekPairRequest(requestId, code).then(
      (v) => {
        limitPairCode(requestId, v.expiresAt);
        if (isPairFinal(v.state)) forgetPairCode(requestId);
        setView(v);
      },
      (e: unknown) => {
        const ui = toUiError(e);
        if (ui.status !== undefined && ui.status >= 400 && ui.status < 500 && ui.status !== 429) {
          forgetPairCode(requestId);
          setRefusal(ui);
        }
      },
    );
  }, [code, requestId]);
  if (refusal) return <>{refusalNotice(refusal)}</>;
  if (view && isPairFinal(view.state)) return <PairEnded view={view} />;
  return (
    <PairSignIn
      view={view}
      providers={signInButtons(providers)}
      busy={busy}
      error={error}
      onProvider={(p) => {
        setBusy(p.id);
        setError(null);
        // Back to this page after the provider: the path only, never the code.
        authClient.startSignIn(p, `/pair/${encodeURIComponent(requestId)}`).catch((e: unknown) => {
          setBusy(null);
          setError(toUiError(e));
        });
      }}
      onOwnerLink={(link) => {
        setBusy('link');
        setError(null);
        authClient
          .signInWithOwnerLink(link)
          .then(() => {
            startData();
            return useMe.getState().load();
          })
          .catch((e: unknown) => setError(toUiError(e)))
          .finally(() => setBusy(null));
      }}
    />
  );
}

/** Signed out shows the sign-in card; everything else goes through the usual gate. */
function LiveGate({ requestId, code }: { requestId: string; code: string | null }) {
  const status = useMe((s) => s.status);
  if (status === 'signed-out') {
    return (
      <PairFrame>
        <SignedOutPair requestId={requestId} code={code} />
      </PairFrame>
    );
  }
  return (
    <AuthGate>
      <PairFrame>
        <LivePair requestId={requestId} code={code} />
      </PairFrame>
    </AuthGate>
  );
}

/** Demo data: /pair/demo?mock=1&pair=<case> (features/pairing/mock.ts). */
function MockPair() {
  const c = mockPairCase(window.location.search);
  const [view, setView] = useState(() => mockPairView(c));
  const [name, setName] = useState(view.machine.hostname);
  const [autoUpdate, setAutoUpdate] = useState(true);
  if (c === 'signed-out') {
    return <PairSignIn view={view} providers={MOCK_PAIR_PROVIDERS} busy={null} error={null} onProvider={() => undefined} onOwnerLink={() => undefined} />;
  }
  const s: PairScreenState =
    c === 'loading'
      ? { phase: 'loading', view: null, error: null, noCode: false }
      : c === 'error'
        ? { phase: 'error', view: null, error: MOCK_ERROR, noCode: false }
        : c === 'not-allowed'
          ? { phase: 'refused', view: null, error: MOCK_NOT_ALLOWED, noCode: false }
          : c === 'no-code'
            ? { phase: 'ready', view: { ...view, state: 'unclaimed', claimedBy: undefined, canDecide: false }, error: null, noCode: true }
            : c === 'open'
              ? { phase: 'ready', view, error: null, noCode: false, toOpen: true }
              : { phase: 'ready', view, error: null, noCode: false };
  return (
    <PairScreen
      s={s}
      name={name}
      onName={setName}
      autoUpdate={autoUpdate}
      onAutoUpdate={setAutoUpdate}
      busy={null}
      actionError={null}
      onApprove={() => setView({ ...view, state: 'approved', canDecide: false, machineId: 'm_demo1', machineName: name, decidedBy: { memberId: 'mem_ada', name: 'Ada Lovelace', at: Date.now() } })}
      onDeny={() => setView({ ...view, state: 'denied', canDecide: false, decidedBy: { memberId: 'mem_ada', name: 'Ada Lovelace', at: Date.now() } })}
      onOpen={() => setView({ ...mockPairView('pending') })}
      onWords={() => setView({ ...mockPairView('pending') })}
      onRetry={() => undefined}
    />
  );
}

export default function Pair() {
  const { requestId = '' } = useParams();
  const mock = isMockRequested();
  // Before anything else (the sign-in screen may send this tab away): take the code from the fragment.
  const [code] = useState(() => (mock ? null : capturePairCode(requestId)));
  useEffect(() => {
    document.title = 'Add a computer · Orgus';
  }, []);
  return (
    <TooltipProvider delayDuration={300}>
      {mock ? (
        <PairFrame>
          <MockPair />
        </PairFrame>
      ) : (
        <LiveGate requestId={requestId} code={code} />
      )}
    </TooltipProvider>
  );
}
